/**
 * PERMISSIONS UTILITY - ProfileId-Based Permission Checks
 * 
 * ⚠️ PERMISSIONS ARE PROFILE-BASED
 * Refer to docs/scaffold-task-permissions.md before changing any rule.
 * Role names MUST NOT be used for authorization.
 * 
 * ====================================================
 * SECURITY ENFORCEMENT - DO NOT MODIFY WITHOUT REVIEW
 * ====================================================
 * 
 * PRINCIPLES:
 * 1. ProfileId is the ONLY source of truth for permissions
 * 2. ProfileIds are immutable and system-defined
 * 3. Tenant Roles are labels only (no permission meaning)
 * 4. UI must FAIL CLOSED (no default Admin behavior)
 * 5. UI must NEVER guess permissions
 * 6. Backend remains the final authority
 * 
 * USAGE:
 * - Import: import { canCreateScaffoldTask, canStartTask, ... } from '@/utils/permissions';
 * - Call with profileId: canCreateScaffoldTask(userProfile?.profileId)
 * - Returns false if profileId is missing (fail-closed)
 * 
 * DO NOT:
 * - Use role names for permissions
 * - Default to Admin access
 * - Infer permissions from UI state
 * - Duplicate permission logic elsewhere
 */

/**
 * Canonical Profile IDs (DO NOT CHANGE)
 * These are system-defined, immutable identifiers
 */
export const Profiles = {
  AppOwner: "BB95D67C-0C34-46FA-A4C0-2386A4B442A5",
  SuperAdmin: "9A3E3947-212B-49CF-8EAC-5C0A37A03C16",
  SupportStaff: "062EAACA-58E8-42A5-82F8-AC5C2798CEDE",
  SystemBot: "9E0889FB-BDC0-433E-9F6F-FEE3C1FA0CC3",

  Admin: "9FB0E270-A6B1-4612-B708-A5CAEDC88BB3",
  OfficeUser: "68EAB8D2-9E14-467D-B159-B3EEAC001556",
  FieldWorker: "D5E6F7A8-B9C0-4123-E456-789ABCDEF012",
  Supervisor: "E6F7A8B9-C0D1-4234-F567-89ABCDEF0123",
  ProjectManager: "F7A8B9C0-D1E2-4345-A678-9ABCDEF01234",
  SiteManager: "A8B9C0D1-E2F3-4456-B789-ABCDEF012345",
  QualityInspector: "B9C0D1E2-F3A4-4567-C89A-BCDEF0123456",
  SalesExecutive: "C0D1E2F3-A4B5-4678-D9AB-CDEF01234567",
  Accountant: "D1E2F3A4-B5C6-4789-EABC-DEF012345678",
  ComplianceUser: "E3F4A5B6-C7D8-4900-FCDE-0123456789AB",
  AccountManager: "F4A5B6C7-D8E9-4901-ADEF-123456789ABC",
  FinManager: "A5B6C7D8-E9F0-4902-BEF0-23456789ABCD",

  ClientUser: "F78EDA37-34F5-4679-8826-DF0854293EA3",
  ClientAdmin: "A1B2C3D4-E5F6-4789-A012-B3C4D5E6F789",
  ClientSiteManager: "B2C3D4E5-F6A7-4890-B123-C4D5E6F7A890",
  ClientJobManager: "C3D4E5F6-A7B8-4901-C234-D5E6F7A8B901",
  ClientViewer: "E2F3A4B5-C6D7-4890-FBCD-EF0123456789",
  ClientScaffoldManager: "B6C7D8E9-F0A1-4903-CF01-3456789ABCDE",
  ClientSupervisorUser: "C7D8E9F0-A1B2-4904-D012-456789ABCDEF"
} as const;

/**
 * Helper to check if profileId matches any of the provided profile IDs
 * FAIL-CLOSED: Returns false if profileId is missing or invalid
 * GUIDs are case-insensitive, so we normalize both sides for comparison
 */
function hasProfileId(profileId: string | null | undefined, allowedProfileIds: string[]): boolean {
  if (!profileId || typeof profileId !== 'string') {
    return false; // FAIL CLOSED - no profileId means no permission
  }
  
  // Normalize profileId to uppercase for case-insensitive GUID comparison
  const normalizedProfileId = profileId.toUpperCase().trim();
  
  // Normalize allowed profile IDs to uppercase for comparison
  const normalizedAllowedIds = allowedProfileIds.map(id => id.toUpperCase().trim());
  
  const matches = normalizedAllowedIds.includes(normalizedProfileId);
  
  // Debug logging in development - log ALL comparisons, not just mismatches
  if (process.env.NODE_ENV === 'development' && profileId) {
    console.log('[hasProfileId] ProfileId check:', {
      providedProfileId: profileId,
      normalizedProfileId: normalizedProfileId,
      allowedProfileIds: allowedProfileIds,
      normalizedAllowedIds: normalizedAllowedIds,
      matches: matches,
      matchDetails: normalizedAllowedIds.map((normalizedAllowedId, idx) => ({
        originalAllowedId: allowedProfileIds[idx],
        normalizedAllowedId: normalizedAllowedId,
        matches: normalizedAllowedId === normalizedProfileId
      }))
    });
  }
  
  return matches;
}

/**
 * PERMISSIONS ARE PROFILE-BASED — DO NOT USE ROLE NAMES
 * 
 * Scaffold Task Permissions
 */

/**
 * Check if user can create scaffold tasks
 * Allowed: Admin OR Supervisor
 */
export function canCreateScaffoldTask(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor
  ]);
}

/**
 * Check if user can start a scaffold task
 * Allowed: Admin OR Supervisor
 */
export function canStartTask(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor
  ]);
}

/**
 * Check if user can complete a scaffold task
 * Allowed: Admin OR Supervisor
 */
export function canCompleteTask(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor
  ]);
}

/**
 * Check if user can assign/reassign crew to scaffold tasks
 * Allowed: Admin OR Supervisor
 */
export function canAssignCrew(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor
  ]);
}

/**
 * Check if user can log time for scaffold tasks
 * Allowed: FieldWorker (assigned only - checked separately in UI)
 * Note: This checks if the profile CAN log time, not if they're assigned
 */
export function canLogTime(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.FieldWorker
  ]);
}

/**
 * Check if user can view all scaffold tasks (regardless of assignment)
 * Allowed: Admin OR Supervisor
 */
export function canViewAllTasks(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor
  ]);
}

/**
 * Check if user is FieldWorker profile
 * Used for assignment-based permission checks
 */
export function isFieldWorkerProfile(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [Profiles.FieldWorker]);
}

/**
 * Check if user is Admin profile
 */
export function isAdminProfile(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [Profiles.Admin]);
}

/**
 * Check if user is Supervisor profile
 */
export function isSupervisorProfile(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [Profiles.Supervisor]);
}

/**
 * Check if user is Admin OR Supervisor (privileged roles)
 */
export function isAdminOrSupervisor(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor
  ]);
}

/**
 * Check if user can create exception tasks (not from Work Order)
 * Allowed: Admin only
 */
export function canCreateExceptionTask(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [Profiles.Admin]);
}

/**
 * Check if user can view timesheets for scaffold tasks
 * Allowed: Admin OR Supervisor OR ProjectManager
 * FieldWorker MUST NOT see this view
 */
export function canViewTaskTimesheets(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor,
    Profiles.ProjectManager
  ]);
}

/**
 * Check if user can view jobs
 * Allowed: Admin OR Supervisor OR ProjectManager OR SiteManager
 * Used for navigation visibility and job list access
 */
export function canViewJob(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor,
    Profiles.ProjectManager,
    Profiles.SiteManager
  ]);
}

/**
 * Check if user can view Scheduler
 * Allowed: Admin (TenantAdmin) OR Manager profiles (Supervisor, ProjectManager, SiteManager)
 * Used for navigation visibility and scheduler access
 * Fail-closed: Returns false if profileId is missing
 */
export function canViewScheduler(profileId: string | null | undefined): boolean {
  return hasProfileId(profileId, [
    Profiles.Admin,
    Profiles.Supervisor,
    Profiles.ProjectManager,
    Profiles.SiteManager
  ]);
}
